/**
 * Cache dos tokens escopados de **preview** usados nos `<img>` da grade.
 *
 * O token é emitido pelo backend (`POST /arquivos/:pedidoId/preview-token`) com
 * a identidade de quem pediu — inclusive `isAdmin`. Guardar esse token sem
 * amarrar à identidade fazia um token **de admin** sobreviver ao logout/login e
 * ser reaproveitado na visão do cliente: a rota `/thumb` então servia a variante
 * **sem marca d'água** (URL `p=0`, `Cache-Control: immutable`), enquanto o
 * preview grande — que usa a sessão real via `Authorization` — vinha marcado.
 *
 * Regras deste módulo:
 * - a chave do cache carrega a perspectiva (`admin:` / `cliente:` / `master:`)
 *   e o `pedidoId`, então identidades diferentes nunca compartilham token;
 * - o `payload` do JWT é conferido antes de ler/gravar: cache antigo ou
 *   divergente é descartado na hora (limpa sozinho o que já foi envenenado);
 * - `limparTokensPreview()` é chamado em toda troca de sessão (logout, 401,
 *   login, início/fim do acesso mestre).
 */
export const PREFIXO_PREVIEW_TOKEN = 'zoompro:preview-token:';

/** Janela de segurança para renovar antes de expirar (segundos). */
export const MARGEM_RENOVACAO_SEGUNDOS = 300;

/**
 * Decodifica o payload de um JWT (base64url) sem validar assinatura.
 *
 * A assinatura é conferida pelo backend; aqui o payload serve apenas para
 * checar **de quem** é o token cacheado.
 *
 * @param {string} token
 * @returns {object|null}
 */
export const decodificarPayloadJwt = (token) => {
  try {
    const partes = String(token || '').split('.');
    if (partes.length < 2 || !partes[1]) return null;

    const base64 = partes[1].replace(/-/g, '+').replace(/_/g, '/');
    const comPadding = base64 + '='.repeat((4 - (base64.length % 4)) % 4);

    return JSON.parse(atob(comPadding));
  } catch {
    return null;
  }
};

/**
 * Perspectiva de quem está pedindo a miniatura.
 *
 * - `master:<id>` — admin usando o acesso mestre (o backend emite o token com
 *   `isAdmin: false`, então a visão é a do cliente);
 * - `admin:<id>` / `cliente:<id>` — sessão normal.
 *
 * @param {{userId?: string|number|null, isAdmin?: boolean, master?: boolean}} [opcoes]
 * @returns {string}
 */
export const perspectivaDePreview = ({ userId, isAdmin = false, master = false } = {}) => {
  const id = userId ? String(userId) : 'anon';

  if (master) return `master:${id}`;

  return `${isAdmin ? 'admin' : 'cliente'}:${id}`;
};

/**
 * O token foi emitido para a perspectiva informada?
 *
 * @param {string} token
 * @param {string} perspectiva
 * @returns {boolean}
 */
export const tokenCombinaComPerspectiva = (token, perspectiva) => {
  const payload = decodificarPayloadJwt(token);
  if (!payload) return false;

  const [tipo, id] = String(perspectiva || '').split(':');
  if (!tipo || !id) return false;

  if (String(payload.id || '') !== String(id)) return false;

  // `master` também usa `isAdmin: false` (é a visão do cliente).
  return Boolean(payload.isAdmin) === (tipo === 'admin');
};

/** Chave do `sessionStorage` para uma perspectiva + pedido. */
export const chaveCachePreview = (perspectiva, pedidoId) =>
  `${PREFIXO_PREVIEW_TOKEN}${perspectiva}:${pedidoId}`;

/**
 * Lê o token cacheado e **descarta** cache inválido/divergente.
 *
 * @param {string} perspectiva
 * @param {string} pedidoId
 * @returns {{token: string, expiraEm: number}|null}
 */
export const lerTokenPreview = (perspectiva, pedidoId) => {
  if (!perspectiva || !pedidoId) return null;

  const chave = chaveCachePreview(perspectiva, pedidoId);

  try {
    const bruto = sessionStorage.getItem(chave);
    if (!bruto) return null;

    const dados = JSON.parse(bruto);
    const valido =
      Boolean(dados?.token) &&
      Number(dados?.expiraEm) > Date.now() &&
      tokenCombinaComPerspectiva(dados.token, perspectiva);

    if (!valido) {
      sessionStorage.removeItem(chave);
      return null;
    }

    return dados;
  } catch {
    return null;
  }
};

/**
 * Persiste o token **apenas** se ele pertence à perspectiva informada.
 *
 * Nunca gravar um token de admin na chave do cliente: o cache do browser
 * (`immutable`) guardaria as miniaturas sem marca por um ano.
 *
 * @param {string} perspectiva
 * @param {string} pedidoId
 * @param {{token?: string, expiraEm?: number}} dados
 */
export const salvarTokenPreview = (perspectiva, pedidoId, dados) => {
  if (!perspectiva || !pedidoId || !dados?.token) return;
  if (!tokenCombinaComPerspectiva(dados.token, perspectiva)) return;

  try {
    sessionStorage.setItem(
      chaveCachePreview(perspectiva, pedidoId),
      JSON.stringify({ token: dados.token, expiraEm: dados.expiraEm })
    );
  } catch {
    // sessionStorage indisponível (modo privado) — segue sem persistir.
  }
};

/**
 * Remove **todos** os tokens de preview cacheados.
 *
 * Obrigatório em qualquer troca de identidade (logout, 401, novo login, início
 * ou fim do acesso mestre): o token carrega `isAdmin`, então reaproveitá-lo
 * entre contas fazia a grade do cliente receber bytes sem marca d'água.
 */
export const limparTokensPreview = () => {
  try {
    const chaves = [];

    for (let i = 0; i < sessionStorage.length; i += 1) {
      const chave = sessionStorage.key(i);
      if (chave?.startsWith(PREFIXO_PREVIEW_TOKEN)) chaves.push(chave);
    }

    chaves.forEach((chave) => sessionStorage.removeItem(chave));
  } catch {
    // sessionStorage indisponível — nada a limpar.
  }
};
